// Chat/Chat.tsx
import React, {useContext, useEffect, useMemo, useState} from 'react';
import {Outlet, useLocation, useNavigate} from 'react-router-dom';
import RoomItem from 'Chat/RoomItem';
import {useMediaQuery} from '@mui/material';
import {useErrorProcessing} from 'Core/components/ErrorProvider';
import {AuthContext, AuthContextType} from 'Auth/AuthContext';
import {useRooms} from './RoomsContext';
import {useTheme} from "Theme/ThemeContext";
import {IRoom} from "Types/chat/models";
import CircularProgress from "Core/components/elements/CircularProgress";
import {FC, FCSC} from "WideLayout/Layouts";
import FindPsychologistButtonsBox from "../FindPsychologistButtonsBox";
import {headerHeight} from "Core/components/Header/Header";
import {DOMAIN_URL} from "Auth/axiosConfig";
import Head from "Core/components/Head";

const Chat: React.FC = () => {
    const {rooms, loadMoreRooms, initialLoading} = useRooms();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const {isAuthenticated, user} = useContext(AuthContext) as AuthContextType;
    const navigate = useNavigate();
    const location = useLocation();
    const isMdOrLarger = useMediaQuery('(min-width: 992px)');
    const {notAuthentication} = useErrorProcessing();
    const {plt} = useTheme();

    useEffect(() => {
        if (!isAuthenticated) {
            notAuthentication();
            return;
        }
    }, [isAuthenticated, notAuthentication]);

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const {scrollTop} = e.currentTarget;
        const isTop = scrollTop === 0;
        if (isTop && rooms.length > 0 && !isLoading) {
            setIsLoading(true);
            loadMoreRooms().finally(() => setIsLoading(false));
        }
    };

    const handleRoomClick = (roomId: number) => {
        navigate(`/chats/room/${roomId}`);
    };

    const isRoomView = location.pathname.includes('/room/');

    const sortedRooms = useMemo(() => {
        if (!user) return rooms;
        const specialRoomIndex = rooms.findIndex((room: IRoom) =>
            room.participants.length === 1 && room.participants[0].id === user.id
        );
        if (specialRoomIndex === -1) {
            return rooms;
        }
        const specialRoom = rooms[specialRoomIndex];
        const otherRooms = [...rooms];
        otherRooms.splice(specialRoomIndex, 1);
        return [specialRoom, ...otherRooms];
    }, [rooms, user]);

    const showRooms = !initialLoading && Array.isArray(sortedRooms) && sortedRooms.length > 0;
    const showNoRoomsMessage = !initialLoading && (!Array.isArray(sortedRooms) || sortedRooms.length === 0);

    return (
        <><Head
            title={'Чаты | Лайф Хелп'}
            description={'Помощь квалифицированных психологов от сервиса Лайф Хелп'}
            robots={'noindex, nofollow'}
            canonicalUrl={`${DOMAIN_URL}/chats`}/>
            <div className={`chat h-100 w-100 ${isMdOrLarger ? 'fr' : 'fc'}`}>
                {initialLoading
                    ? <CircularProgress size={'120px'}/>
                    : (isMdOrLarger || !isRoomView) && showRooms
                        ? <FC cls={'rooms'} g={1} minW={350} p={2} maxH={`calc(100vh - ${headerHeight}px)`}
                              scroll={'y-auto'} onScroll={handleScroll}>
                            {sortedRooms.map(room => (
                                <RoomItem key={room.id} room={room} onClick={() => handleRoomClick(room.id)}/>
                            ))}
                            {isLoading && <p>Loading...</p>}
                        </FC>
                        : showNoRoomsMessage &&
                        <FCSC g={1} p={2} scroll={'y-auto'}
                              cls={'no-scrollbar'} textAlign={'center'}>
                            <p>Запишитесь на консультацию и тут будет чат с вашим психологом</p>
                            <FindPsychologistButtonsBox cls={'maxw-250px mx-auto'}/>
                        </FCSC>}
                {!initialLoading && !isLoading && (isMdOrLarger || isRoomView) && (
                    <FC grow w={'100%'} minW={350}>
                        <Outlet/>
                    </FC>
                )}
            </div>
        </>
    );
};

export default Chat;